import type { PermissionCatalog, PermissionCatalogSpec, PermissionString } from './types';

/**
 * Produces a module's typed permission catalog (Addendum A.2 "ownership" section).
 *
 * The keys map 1:1 to the strings the auth service issues today — the backend-alignment CI check
 * diffs `permissions-registry.json` (generated from these catalogs) against the exported backend
 * permission list, so drift fails the build instead of failing silently at runtime.
 *
 * `catalog.groupPolicies.view` is a typed literal, which is the whole point: a typo is a compile
 * error, not a permission that quietly denies access in production.
 */
export function definePermissions<const S extends PermissionCatalogSpec>(
  spec: S,
): PermissionCatalog<S> {
  const out: Record<string, Record<string, string>> = {};
  const all: PermissionString[] = [];

  for (const [key, entry] of Object.entries(spec)) {
    const actions: Record<string, string> = {};
    for (const action of entry.actions) {
      // Contextually typed: with `PermissionString` as the expected type TS keeps the template
      // literal shape instead of widening it to `string`, so no cast is needed.
      const permission: PermissionString = `${entry.domain}.${entry.entity}.${action}`;
      actions[action] = permission;
      all.push(permission);
    }
    out[key] = actions;
  }

  return Object.freeze({ ...out, __all: Object.freeze(all) }) as PermissionCatalog<S>;
}

/** Flattens any number of module catalogs into the aggregated app catalog `core` expands against. */
export function aggregateCatalogs(
  catalogs: readonly { readonly __all: readonly PermissionString[] }[],
): PermissionString[] {
  return [...new Set(catalogs.flatMap((c) => c.__all))].sort();
}
